// Copyright (c) Move Closer 2022.

import { Component, Mixins } from 'vue-property-decorator'
import {
  AnyObject,
  EventbusType,
  EventPayload,
  IEventbus,
  IModal,
  ModalType,
  ResourceActionFailed
} from '@movecloser/front-core'
import throttle from 'lodash/throttle'

import { DrawerLayout } from '../../../../dsl/molecules/Drawer/Drawer.config'
import { DrawerType, IDrawer } from '../../../shared/contracts/services'
import { Inject, logger } from '../../../../support'
import { CartAnalyticsMixin } from '../../../shared/mixins/cart-analytics.mixin'
import { StructureConfigurable } from '../../../../support/mixins'
import { UseListeners } from '../../../../modules/mixins/UseListeners'

import { Drawers, Modals } from '../../config'
import { ToastMixin } from '../../../shared'
import { toImageProps } from '../../../shared/support'

import { CartMutationTypes } from '../../contracts'

import { BaseCartMixin, IBaseCart } from './base-cart.mixin'
import { AllowedAttributes, CartItemData, ProductData, Variant } from '../../../../contexts'
import { CartAddedItemData } from '../../services/cart'
import { RouteName as CheckoutRoutes } from '../../routes'
import { AddedToCartModalPayload } from '../../organisms/AddedToCartModal/AddedToCartModal.contracts'
import { IProductsRepository, ProductsRepositoryType } from '../../../products/contracts/repositories'

declare global {
  interface Window {
    addToCart: (...args: any[]) => void
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
@Component
export class ProductCartMixin extends Mixins<IBaseCart, ToastMixin, CartAnalyticsMixin, StructureConfigurable, UseListeners>(
  BaseCartMixin,
  ToastMixin,
  CartAnalyticsMixin,
  StructureConfigurable,
  UseListeners
) {
  @Inject(DrawerType, false)
  protected readonly drawerConnector?: IDrawer

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Inject(ModalType)
  protected readonly modalConnector!: IModal

  @Inject(ProductsRepositoryType)
  protected readonly productsRepository!: IProductsRepository

  public mobileNavHeight: number | null = null
  public drawerHeight: number | null = null

  /**
   * Handles adding product to cart.
   *
   * FIXME: Here should be generic ProductVariant contract from context.
   */
  public async addToCart (
    product: AnyObject,
    quantity: number = 1,
    openModal: boolean = true,
    modalSize: string = 'medium',
    useDrawer: boolean = false,
    shouldHaveAction: boolean = true
  ): Promise<void> {
    const bundledSkus = product.attributes[AllowedAttributes.BundledProducts] as string[]
    const bundles = product.attributes[AllowedAttributes.IsInBundles] as unknown as Record<string, string[]>
    let bundledSkusMap: CartAddedItemData[] = []

    try {
      if (this.cartId === null) {
        this.isLoggedInUser ? await this.createCustomerCart() : await this.createCart()
      }
      const shouldTrackInitCart = this.cart?.items.length === 0

      let parentSkus: Array<string> = []
      if (bundles) {
        parentSkus = ProductCartMixin.getParentSkus(bundles)
      }

      let cart
      let addedProduct
      let filtered

      if (bundledSkus) {
        bundledSkusMap = bundledSkus.map((sku: string) => ({ sku, quantity }))

        cart = await this.cartService.addSimpleProductsToCart(this.cartId, bundledSkusMap.map((item: CartAddedItemData) => ({ data: item })))
      } else {
        cart = await this.cartService.addToCart(this.cartId, product.sku, quantity)
        filtered = cart.items.filter(item => {
          const variant = this.getFirstVariant(item)
          return variant?.sku === product.sku
        })
        addedProduct = (filtered.length > 0) ? [filtered[0]] : null
      }

      if (addedProduct && cart) {
        // TODO: Test if it works after changes in `getCartAddPayload`
        this.eventBus.emit('app:cart.add', this.getCartAddPayload(addedProduct, quantity, cart))
        this.eventBus.emit('app:cart.view', this.getCartViewPayload(cart))

        if (shouldTrackInitCart) {
          this.eventBus.emit('app:cart.init', cart.id)
        }
      }

      this.$store.commit(CartMutationTypes.SetCartId, cart ? cart.id : null)
      this.$store.commit(CartMutationTypes.SetCart, cart ?? null)

      const shouldRedirect = cart.hasIncorrectQuantities()

      const payload: AddedToCartModalPayload = {
        product: {
          brand: product.attributes.brand ?? '',
          category: product.attributes.mainCategory ?? '',
          description: product.description,
          image: (Array.isArray(product.images) && product.images.length > 0) ? product.images[0] : {
            src: '',
            alt: ''
          },
          bundledSiblings: parentSkus.length > 0 ? parentSkus : [],
          isFaF: product.attributes.isFaF ?? false,
          price: product.price.finalPrice,
          previousPrice: product.price.previousPrice,
          sku: product.sku,
          title: product.name,
          volume: product.attributes.volumeName ?? ''
        },
        shouldHaveAction: shouldHaveAction,
        drawerHeight: this.drawerHeight,
        mobileNavHeight: this.mobileNavHeight,
        onClose: shouldRedirect ? async () => {
          try {
            if (this.$route.name !== `checkout.${CheckoutRoutes.Cart}`) {
              await this.$router.push({ name: `checkout.${CheckoutRoutes.Cart}` })
            }
          } finally {
            this.modalConnector.close()
          }
        } : undefined
      }

      if (useDrawer) {
        if (!this.drawerConnector) {
          logger('[drawerConnector] is of type undefined!')
          return
        }

        this.drawerConnector.open(Drawers.AddedToCard, payload, {
          closeOnClickOutside: true,
          layout: DrawerLayout.Partial
        })
      } else {
        if (openModal) {
          this.modalConnector.open(Modals.AddedToCartModal, payload, { size: modalSize })
        }
      }
    } catch (e) {
      const error = e as Error
      this.showToast(error.message, 'danger')
      logger(e, 'error')
    }
  }

  public async addToCartByProductSku (sku: string): Promise<void> {
    try {
      const productsResponse: ProductData[] = await this.productsRepository.loadProductsBySkus([sku])

      /** Collect all variants from productsResponse */
      const variants = []
      for (const item of Object.values(productsResponse)) {
        for (const variant of Object.values(item.variants)) {
          variants.push(variant)
        }
      }

      const variant = variants.find((item) => item.sku === sku)
      if (!variant) {
        throw new Error(this.$t('front.shared.cartItem.unavailable').toString())
      }

      await this.addToCart(variant, 1, false)
    } catch (e) {
      const error = e as Error
      this.showToast(error.message, 'danger')
      logger(e, 'warn')
    }
  }

  public onResize (): void {
    this.drawerHeight = window.visualViewport.height - (this.mobileNavHeight ? this.mobileNavHeight : 0)
  }

  private static getParentSkus (bundles: Record<string, Array<string>>): Array<string> {
    return Object.keys(bundles).map((parentSku) => parentSku.split('_')[1].split('_')[0])
  }

  created () {
    this.eventBus.handle('app:mobile-nav-height', (event: EventPayload<number>) => {
      if (event.payload) {
        this.mobileNavHeight = event.payload
        this.drawerHeight = window.visualViewport.height - (this.mobileNavHeight ? this.mobileNavHeight : 0)
      }
    })
  }

  mounted () {
    this.addEventListener('resize', throttle(this.onResize, 100))
  }
}
